import { supabase } from './supabase'
import { User } from '@supabase/supabase-js'
import { v4 as uuidv4 } from 'uuid'
import { getMappedMediaOutlet } from './mediaOutletMapper'

// Define Brand type (adjust based on actual Brands table schema)
export type Brand = {
  id: string; // uuid
  brand_name?: string | null;
  logo_url?: string | null;
  // Add other fields if needed
  company_name?: string | null;
  business_location?: string | null;
  local_brand_name?: string | null;
  website?: string | null;
  business_description?: string | null;
  brand_type?: 'Corporate' | 'Personal' | null;
  created_at: string;
  updated_at: string;
}

export type NewsClipping = {
  id: number | string
  title: string  // Mapped from Title in DB
  source: string  // Mapped from Source in DB
  date: string  // Mapped from Date in DB
  url?: string  // Mapped from URL in DB
  content?: string  // Mapped from Description in DB
  category?: string  // Mapped from Category in DB
  sentiment?: string  // Mapped from Sentiment in DB
  brandName?: string  // Mapped from Brand name in DB
  summary?: string  // Mapped from Summary_ENG in DB
  summary_original?: string  // Mapped from Summary_Original in DB
  user_id?: string  // ID of the user who created the clip
  image_url?: string  // URL of the news article image
  quote_1?: string | null // Quote 1 from the article
  quote_2?: string | null // Quote 2 from the article
  quote_3?: string | null // Quote 3 from the article
  local_brand_name?: string | null // Added from joined Brands table
  visibility?: string // Mapped from Visibility in DB
  reach?: number | null // Calculated reach value
  visitors?: number | null // Visitors count from media_outlets table
  media_type?: string | null // Media type (Tier 1/2/3) from media_outlets table
  Language?: string; // Mapped from Language in DB
}

// Helper to ensure consistent ID handling (string or number)
const ensureNumericId = (id: number | string): number => {
  if (typeof id === 'string') {
    return parseInt(id, 10)
  }
  return id
}

// Define type for brand access filtering
export type BrandAccessFilter = {
  type: 'all' | 'specific' | 'none';
  names?: string[]; // Only used when type is 'specific'
}

export async function fetchNewsClippings(
  userId?: string,
  allowedBrandAccess?: BrandAccessFilter // Optional brand access parameter
) {
  try {
    // Early exit if user has no brand access
    if (allowedBrandAccess?.type === 'none') {
        console.log('fetchNewsClippings: User has no brand access, returning empty array.');
        return [];
    }

    let query = supabase
      .from('NewsClipping')
      .select('*, Language')
      .not('Category', 'is', null)
      .neq('Category', '')
      .neq('Category', 'Unmentioned') // Also exclude the string "Unmentioned"
      .order('Date', { ascending: false })
      .limit(100) // Prevent query timeout - fetch only latest 100 results
    
    // Filter by user_id if provided
    if (userId) {
      query = query.eq('user_id', userId)
    }
    
    // Filter by specific brand names if applicable
    if (allowedBrandAccess?.type === 'specific') {
      if (allowedBrandAccess.names && allowedBrandAccess.names.length > 0) {
        console.log('fetchNewsClippings: Filtering by specific brand names:', allowedBrandAccess.names);
        query = query.in('Brand name', allowedBrandAccess.names);
      } else {
        // If type is specific but no names are provided, return empty (no access)
        console.log('fetchNewsClippings: Specific brand access selected, but no brand names provided. Returning empty array.');
        return [];
      }
    }
    // If type is 'all' or undefined, no brand name filtering is applied
    else {
        console.log('fetchNewsClippings: Access type is \'all\' or undefined, fetching all accessible clippings.');
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching news clippings:', error)
      return []
    }

    // Transform data to match our application's expected format
    return Promise.all(data.map(async (clip: any) => {
      const rawSource = clip.Source || clip.source || 'Unknown Source';
      let sourceForLookup = rawSource.toLowerCase();
      if (sourceForLookup === 'new.cyprusnews.eu') {
        sourceForLookup = 'cyprusnews.eu';
      }
      
      const outletInfo = await getMappedMediaOutlet(sourceForLookup);
      
      return {
        id: clip.id,
        title: clip.Title || clip.title || 'Untitled Clip',
        source: rawSource,
        date: clip.Date || clip.date || new Date().toLocaleDateString(),
        content: clip.Description || clip.description,
        url: clip.URL || clip.url,
        category: clip.Category || clip.category,
        sentiment: clip.Sentiment || clip.sentiment,
        brandName: clip['Brand name'] || clip.brand_name || clip.brandName,
        summary: clip.Summary_ENG || clip.summary_eng || clip.summary,
        summary_original: clip.Summary_Original || clip.summary_original,
        user_id: clip.user_id,
        image_url: clip.image_url,
        quote_1: clip.quote_1,
        quote_2: clip.quote_2,
        quote_3: clip.quote_3,
        visibility: clip.Visibility || clip.visibility,
        reach: clip.Reach || clip.reach,
        Language: clip.Language || clip.language,
        visitors: outletInfo?.visitors ?? null,
        media_type: outletInfo?.mediaType ?? null,
      };
    }));
  } catch (error) {
    console.error('Error in fetchNewsClippings:', error)
    return []
  }
}

export async function fetchNewsClippingById(id: number | string) {
  try {
    const numericId = ensureNumericId(id)
    
    const { data, error } = await supabase
      .from('NewsClipping')
      .select(`
        *,
        Language, 
        Brands ( local_brand_name )
      `)
      .eq('id', numericId)
      .single()

    if (error) {
      console.error(`Error fetching news clipping with ID ${id}:`, error)
      return null
    }
    
    const brandData = Array.isArray(data.Brands) ? data.Brands[0] : data.Brands;
    const localBrandName = brandData?.local_brand_name || null;

    const rawSource = data.Source || data.source || 'Unknown Source';
    let sourceForLookup = rawSource.toLowerCase();
    if (sourceForLookup === 'new.cyprusnews.eu') {
      sourceForLookup = 'cyprusnews.eu';
    }
    const outletInfo = await getMappedMediaOutlet(sourceForLookup);

    return {
      id: data.id,
      title: data.Title || data.title || 'Untitled Clip',
      source: rawSource,
      date: data.Date || data.date || new Date().toLocaleDateString(),
      content: data.Description || data.description,
      url: data.URL || data.url,
      category: data.Category || data.category,
      sentiment: data.Sentiment || data.sentiment,
      brandName: data['Brand name'] || data.brand_name || data.brandName,
      summary: data.Summary_ENG || data.summary_eng || data.summary,
      summary_original: data.Summary_Original || data.summary_original,
      user_id: data.user_id,
      image_url: data.image_url,
      quote_1: data.quote_1,
      quote_2: data.quote_2,
      quote_3: data.quote_3,
      local_brand_name: localBrandName,
      visibility: data.Visibility || data.visibility,
      reach: data.Reach || data.reach,
      Language: data.Language || data.language,
      visitors: outletInfo?.visitors ?? null,
      media_type: outletInfo?.mediaType ?? null,
    }
  } catch (error) {
    console.error('Error in fetchNewsClippingById:', error)
    return null
  }
}

export async function createNewsClipping(clippingData: Partial<NewsClipping>): Promise<NewsClipping> {
  // Map our app model fields to database column names
  const dbData: any = {
    Title: clippingData.title,
    Source: clippingData.source,
    Date: clippingData.date,
    URL: clippingData.url,
    Description: clippingData.content,
    Category: clippingData.category,
    Sentiment: clippingData.sentiment,
    'Brand name': clippingData.brandName, // Map brandName to 'Brand name' with space
    Summary_ENG: clippingData.summary,
    Summary_Original: clippingData.summary_original,
    user_id: clippingData.user_id,
    image_url: clippingData.image_url,
    quote_1: clippingData.quote_1,
    quote_2: clippingData.quote_2,
    quote_3: clippingData.quote_3,
    Visibility: clippingData.visibility,
    Language: clippingData.Language
  };

  const { data, error } = await supabase
    .from('NewsClipping')
    .insert([dbData])
    .select()
    .single();
    
  if (error) throw error;
  return mapDbToNewsClipping(data);
}

export async function saveClipping(userId: string, clippingId: number | string) {
  const numericClippingId = ensureNumericId(clippingId)
  console.log(`[saveClipping] Attempting to save: userId=${userId}, clippingId=${numericClippingId}`);
  
  // Check if the clipping is already saved by this user
  try {
    const { data: existingSave, error: checkError } = await supabase
      .from('SavedClippings')
      .select('id') // Select a minimal column like id
      .eq('user_id', userId)
      .eq('clipping_id', numericClippingId)
      .maybeSingle()

    if (checkError) {
      console.error('[saveClipping] Error checking for existing save:', checkError);
      // Decide if we should throw or try to insert anyway? Let's try inserting.
    } else if (existingSave) {
      console.log('[saveClipping] Clipping already saved, returning true.');
      return true;
    }
  } catch (err) {
      console.error('[saveClipping] Exception during existing save check:', err);
      // Fall through to attempt insert
  }

  // If it doesn't exist or check failed, try to create it
  console.log(`[saveClipping] Attempting insert...`);
  const { data: insertData, error: insertError } = await supabase
    .from('SavedClippings')
    .insert({
      user_id: userId,
      clipping_id: numericClippingId,
      saved_at: new Date().toISOString()
    })
    .select(); // Add select() to potentially get back the inserted row

  if (insertError) {
    console.error('[saveClipping] Error inserting save record:', insertError);
    throw insertError; // Rethrow to be caught by the calling function
  }

  console.log('[saveClipping] Insert successful:', insertData);
  return true;
}

export async function fetchSavedClippings(userId: string) {
  console.log(`[fetchSavedClippings] Fetching saved clips for userId=${userId}`);
  try {
    // --- Restore original join query --- 
    const { data, error } = await supabase
      .from('SavedClippings')
      .select(`
        clipping_id,
        NewsClipping:clipping_id (*)
      `)
      .eq('user_id', userId)
      .order('saved_at', { ascending: false });

    if (error) {
      // If the error is about the table not existing, it means no saved clips
      if (error.message && (
          error.message.includes('does not exist') || 
          error.message.includes('relation') ||
          error.message.includes('undefined table')
        )) {
        console.log('[fetchSavedClippings] SavedClippings table does not exist, returning empty array');
        return [];
      }
      
      console.error('[fetchSavedClippings] Error fetching saved clippings with join:', error);
      return [];
    }

    // Make sure data exists and is an array
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.log('[fetchSavedClippings] No saved records found for user (after join attempt).');
      return [];
    }
    
    // --- DEBUG: Log the raw joined data structure --- 
    console.log(`[fetchSavedClippings] Found ${data.length} records (raw joined):`, JSON.stringify(data, null, 2));
    
    // --- Original Mapping Logic --- 
    const mappedData = data
      .filter(item => {
        // --- DEBUG: Log items being filtered --- 
        const hasNewsClipping = !!item.NewsClipping;
        if (!hasNewsClipping) {
          console.warn('[fetchSavedClippings] Filtering out item due to missing NewsClipping relation:', item);
        }
        return hasNewsClipping;
      }) 
      .map((item: any) => {
        // --- DEBUG: Log item before mapping --- 
        // console.log('[fetchSavedClippings] Mapping item:', item);
        return {
          id: item.NewsClipping.id,
          title: item.NewsClipping.Title || 'Untitled Clip',
          source: item.NewsClipping.Source || 'Unknown Source',
          date: item.NewsClipping.Date || new Date().toLocaleDateString(),
          content: item.NewsClipping.Description,
          url: item.NewsClipping.URL,
          category: item.NewsClipping.Category,
          sentiment: item.NewsClipping.Sentiment,
          brandName: item.NewsClipping['Brand name'],
          summary: item.NewsClipping.Summary_ENG,
          user_id: item.NewsClipping.user_id,
          image_url: item.NewsClipping.image_url,
          // Add quote fields (assuming DB columns are quote_1, quote_2, quote_3)
          quote_1: item.NewsClipping.quote_1,
          quote_2: item.NewsClipping.quote_2,
          quote_3: item.NewsClipping.quote_3
        };
      });
      
    console.log(`[fetchSavedClippings] Mapped data count: ${mappedData.length}`);
    return mappedData;

  } catch (error) {
    console.error('Error in fetchSavedClippings:', error)
    return []
  }
}

export async function isClippingSaved(userId: string, clippingId: number | string) {
  try {
    const numericId = ensureNumericId(clippingId)

    const { data, error } = await supabase
      .from('SavedClippings')
      .select('*')
      .eq('user_id', userId)
      .eq('clipping_id', numericId)
      .maybeSingle()

    if (error) {
      // If the error is about the table not existing, it means no saved clips
      if (error.message && (
          error.message.includes('does not exist') || 
          error.message.includes('relation') ||
          error.message.includes('undefined table')
        )) {
        return false
      }
      
      console.error('Error checking if clipping is saved:', error)
      throw error
    }

    return !!data
  } catch (error) {
    console.error('Error in isClippingSaved:', error)
    return false
  }
}

export async function updateNewsClipping(id: number | string, updates: Partial<NewsClipping>) {
  try {
    const numericId = ensureNumericId(id)
    
    // Map from app model to database model
    const dbUpdates: any = {}
    
    if (updates.title !== undefined) dbUpdates.Title = updates.title
    if (updates.source !== undefined) dbUpdates.Source = updates.source
    if (updates.date !== undefined) dbUpdates.Date = updates.date
    if (updates.url !== undefined) dbUpdates.URL = updates.url
    if (updates.content !== undefined) dbUpdates.Description = updates.content
    if (updates.category !== undefined) dbUpdates.Category = updates.category
    if (updates.sentiment !== undefined) dbUpdates.Sentiment = updates.sentiment
    if (updates.brandName !== undefined) dbUpdates['Brand name'] = updates.brandName
    if (updates.summary !== undefined) dbUpdates.Summary_ENG = updates.summary
    if (updates.summary_original !== undefined) dbUpdates.Summary_Original = updates.summary_original
    if (updates.user_id !== undefined) dbUpdates.user_id = updates.user_id
    if (updates.image_url !== undefined) dbUpdates.image_url = updates.image_url
    if (updates.visibility !== undefined) dbUpdates.Visibility = updates.visibility
    if (updates.Language !== undefined) dbUpdates.Language = updates.Language;

    console.log('Updating news clipping:', { id: numericId, updates: dbUpdates })

    // Update with better error handling for RLS issues
    const { error: updateError } = await supabase
      .from('NewsClipping')
      .update(dbUpdates)
      .eq('id', numericId)
      
    if (updateError) {
      if (updateError.code === '42501') { // Permission denied error code
        console.error('RLS policy prevented update. Please check Supabase RLS policies:', updateError)
        console.log('Additional error details:', updateError.details, updateError.hint, updateError.message)
        
        // Provide a more specific error message
        throw new Error(`RLS policy violation: ${updateError.message}. Contact your database administrator to enable appropriate permissions.`)
      } else {
        console.error('Error updating record:', updateError)
        throw updateError
      }
    }

    // Try to fetch the updated record
    try {
      const { data: updatedData, error: fetchError } = await supabase
        .from('NewsClipping')
        .select('*')
        .eq('id', numericId)
        .single()

      if (fetchError) {
        console.error(`Error fetching updated news clipping with ID ${id}:`, fetchError)
        // Return a partial record with updated fields to indicate success
        return {
          id: numericId,
          ...updates,
          title: updates.title || '',
          source: updates.source || ''
        } as NewsClipping
      }

      return mapDbToNewsClipping(updatedData)
    } catch (fetchErr) {
      console.error('Error fetching record after update:', fetchErr)
      // Return a partial record with updated fields to indicate success
      return {
        id: numericId,
        ...updates,
        title: updates.title || '',
        source: updates.source || ''
      } as NewsClipping
    }
  } catch (error) {
    console.error('Error in updateNewsClipping:', error)
    throw error
  }
}

export async function updateUserProfile(userId: string, profileData: { name?: string, email?: string, bio?: string }) {
  try {
    console.log('Updating profile for user:', userId, 'with data:', profileData)

    // Fetch the current profile to check if it exists
    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle()
      
    if (checkError) {
      if (checkError.message && (
        checkError.message.includes('does not exist') || 
        checkError.message.includes('relation'))) {
        // If table doesn't exist, log error. It should be created via migrations.
        console.error('Table profiles does not exist. It must be created via migrations.', checkError)
      } else {
        console.error('Error checking for existing profile:', checkError)
        // Continue anyway to try insert/update
      }
    }
    
    // If profile exists, update it; otherwise insert a new one
    if (existingProfile) {
      // Prepare update payload, mapping 'name' to 'full_name'
      const updatePayload: { [key: string]: any } = { updated_at: new Date().toISOString() };
      if (profileData.name) {
        updatePayload.full_name = profileData.name; // Map to full_name
      }
      // Add other fields like bio if they exist in profileData
      if (profileData.bio) {
        updatePayload.bio = profileData.bio;
      }
      
      // Update existing profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update(updatePayload) // Use the mapped payload
        .eq('id', userId)
        
      if (updateError) {
        console.error('Error updating profile:', updateError)
        throw updateError
      }
    } else {
      // Prepare insert payload, mapping 'name' to 'full_name'
      const insertPayload: { [key: string]: any } = {
        id: userId,
        updated_at: new Date().toISOString()
      };
      if (profileData.name) {
        insertPayload.full_name = profileData.name; // Map to full_name
      }
      if (profileData.bio) {
        insertPayload.bio = profileData.bio;
      }

      // Insert new profile
      const { error: insertError } = await supabase
        .from('profiles')
        .insert(insertPayload) // Use the mapped payload
        
      if (insertError) {
        // If insert fails due to table not existing, log error. It should be created via migrations.
        console.error('Table profiles does not exist. Cannot insert profile.', insertError)
        
        // Rethrow the error as we can't proceed
        throw insertError
      }
    }
    
    // --- NEW: Update user_metadata in Supabase Auth as well --- 
    if (profileData.name) {
      try {
        const { error: metaUpdateError } = await supabase.auth.updateUser({
          data: { 
            full_name: profileData.name 
          } 
        })
        
        if (metaUpdateError) {
          console.warn('Error updating user_metadata (continuing):', metaUpdateError)
          // Don't throw here, profile update was successful, but log the warning
        }
      } catch (metaError) {
        console.warn('Exception updating user_metadata (continuing):', metaError)
      }
    }
    // --- End of new section --- 

    // Update the email in auth table if provided
    if (profileData.email) {
      try {
        const { error: emailUpdateError } = await supabase.auth.updateUser({
          email: profileData.email
        })
        
        if (emailUpdateError) {
          console.warn('Error updating email (continuing with profile update):', emailUpdateError)
        }
      } catch (emailError) {
        console.warn('Exception updating email (continuing with profile update):', emailError)
      }
    }
  } catch (error) {
    console.error('Error in updateUserProfile:', error)
    throw error
  }
}

export async function deleteNewsClipping(id: number | string) {
  try {
    const numericId = ensureNumericId(id)
    
    const { error } = await supabase
      .from('NewsClipping')
      .delete()
      .eq('id', numericId)

    if (error) {
      console.error(`Error deleting news clipping with ID ${id}:`, error)
      throw error
    }

    return true
  } catch (error) {
    console.error('Error in deleteNewsClipping:', error)
    throw error
  }
}

// --- NEW: Function to fetch brand names from IDs ---
export async function fetchBrandNamesByIds(brandIds: string[]): Promise<string[]> {
    if (!brandIds || brandIds.length === 0) {
        return [];
    }

    try {
        const { data, error } = await supabase
            .from('Brands') // Assuming the table name is 'Brands' (case-sensitive)
            .select('Name')    // Use Name instead of brand_name (correct column name)
            .in('id', brandIds); // Assuming the primary key column is 'id'

        if (error) {
            console.error('Error fetching brand names by IDs:', error);
            return [];
        }

        // Extract names from the data
        return data ? data.map(brand => brand.Name).filter(Boolean) as string[] : [];

    } catch (error) {
        console.error('Exception in fetchBrandNamesByIds:', error);
        return [];
    }
}
// --------------------------------------------------- 

// --- Add the unsaveClipping function back ---
export async function unsaveClipping(userId: string, clippingId: number | string) {
  console.log(`[unsaveClipping] Attempting to unsave: userId=${userId}, clippingId=${clippingId}`);
  try {
    const numericId = ensureNumericId(clippingId);

    console.log(`[unsaveClipping] Executing delete...`); // Restore the content of the log
    const { error, count } = await supabase
      .from('SavedClippings')
      .delete({ count: 'exact' }) // Request count of deleted rows
      .eq('user_id', userId)
      .eq('clipping_id', numericId);

    if (error) {
      // Don't throw error if table doesn't exist, just log and return
      if (error.message && (error.message.includes('does not exist') || error.message.includes('relation'))) {
        console.warn('[unsaveClipping] SavedClippings table does not exist during unsave attempt.');
        return true; // Consider it successful as there's nothing to unsave
      }
      console.error('[unsaveClipping] Error deleting save record:', error);
      throw error; // Rethrow other errors
    }

    console.log(`[unsaveClipping] Delete successful. Rows deleted: ${count}`);
    return true;
  } catch (error) {
    console.error('[unsaveClipping] Exception:', error);
    // Don't rethrow, just return false or handle appropriately
    return false; 
  }
}
// ------------------------------------------- 

// --- NEW: Function to fetch all brands --- 
export async function fetchBrands(): Promise<Brand[]> {
  try {
    const { data, error } = await supabase
      .from('Brands') // Use the correct table name
      .select('id, Name, logo_url, company_name, business_location, local_brand_name, website, business_description, brand_type, created_at, updated_at') // Select desired columns
      .order('Name', { ascending: true }); // Optional: order by name

    if (error) {
      console.error('Error fetching brands:', error);
      return [];
    }

    // Type assertion to match the Brand type - map Name to brand_name for compatibility
    const mappedData = data?.map(brand => ({
      ...brand,
      brand_name: brand.Name // Map Name to brand_name for type compatibility
    })) || [];

    return mappedData as Brand[]; 

  } catch (error) {
    console.error('Error in fetchBrands:', error);
    return [];
  }
}

/**
 * Fetch filter counts directly from database with dynamic filtering
 */
export async function fetchFilterCounts(filters: Record<string, string[]> = {}) {
  try {
    console.log(`[fetchFilterCounts] Fetching counts with filters:`, JSON.stringify(filters));
    
    // Initialize response structure
    const counts: Record<string, Record<string, number>> = {
      brand: {},
      category: {},
      sentiment: {},
      source: {}
    };

    // Helper function to apply filters except for excluded field
    const applyFiltersExcept = (query: any, excludeField: string) => {
      // Apply date filters (always apply these)
      if (filters.startDate && filters.startDate.length > 0 &&
          filters.endDate && filters.endDate.length > 0) {
        query = query.gte('Date', filters.startDate[0])
                    .lte('Date', filters.endDate[0]);
      }
      
      // Apply category filters
      if (excludeField !== 'category' && filters.category && filters.category.length > 0) {
        query = query.in('Category', filters.category);
      }
      
      // Apply brand filters
      if (excludeField !== 'brandName' && filters.brandName && filters.brandName.length > 0) {
        query = query.in('Brand name', filters.brandName);
      }
      
      // Apply sentiment filters
      if (excludeField !== 'sentiment' && filters.sentiment && filters.sentiment.length > 0) {
        query = query.in('Sentiment', filters.sentiment);
      }
      
      // Apply source filters
      if (excludeField !== 'source' && filters.source && filters.source.length > 0) {
        query = query.in('Source', filters.source);
      }
      
      return query;
    };

    // Get brand counts
    let brandQuery = supabase.from('NewsClipping').select('*');
    brandQuery = applyFiltersExcept(brandQuery, 'brandName');
    const { data: brandData, error: brandError } = await brandQuery;
    
    if (brandError) {
      console.error('[fetchFilterCounts] Error fetching brand counts:', brandError);
    } else if (brandData) {
      // Count brands
      brandData.forEach(item => {
        const brand = item['Brand name'];
        if (brand) {
          counts.brand[brand] = (counts.brand[brand] || 0) + 1;
        }
      });
      console.log(`[fetchFilterCounts] Found ${Object.keys(counts.brand).length} brands with counts`);
      
      // Check for problematic brands
      if (brandData.length > 0) {
        const problematicBrands = ["Nikos Christodoulides", "Pizza Hut"];
        problematicBrands.forEach(brand => {
          const count = counts.brand[brand] || 0;
          console.log(`[fetchFilterCounts] Brand "${brand}" count: ${count}`);
          
          // If we have clippings but no count for these brands, log a warning
          if (count === 0) {
            console.log(`[fetchFilterCounts] Searching for "${brand}" in data...`);
            const found = brandData.filter(item => 
              item['Brand name'] === brand || 
              item.brandName === brand || 
              item.brand_name === brand
            );
            console.log(`[fetchFilterCounts] Found ${found.length} raw records for "${brand}"`);
          }
        });
      }
    }
    
    // Get category counts
    let categoryQuery = supabase.from('NewsClipping').select('*');
    categoryQuery = applyFiltersExcept(categoryQuery, 'category');
    const { data: categoryData, error: categoryError } = await categoryQuery;
    
    if (categoryError) {
      console.error('[fetchFilterCounts] Error fetching category counts:', categoryError);
    } else if (categoryData) {
      // Count categories
      categoryData.forEach(item => {
        const category = item.Category;
        if (category) {
          counts.category[category] = (counts.category[category] || 0) + 1;
        }
      });
      console.log(`[fetchFilterCounts] Found ${Object.keys(counts.category).length} categories with counts`);
    }
    
    // Get sentiment counts
    let sentimentQuery = supabase.from('NewsClipping').select('*');
    sentimentQuery = applyFiltersExcept(sentimentQuery, 'sentiment');
    const { data: sentimentData, error: sentimentError } = await sentimentQuery;
    
    if (sentimentError) {
      console.error('[fetchFilterCounts] Error fetching sentiment counts:', sentimentError);
    } else if (sentimentData) {
      // Count sentiments
      sentimentData.forEach(item => {
        const sentiment = item.Sentiment;
        if (sentiment) {
          counts.sentiment[sentiment] = (counts.sentiment[sentiment] || 0) + 1;
        }
      });
      console.log(`[fetchFilterCounts] Found ${Object.keys(counts.sentiment).length} sentiments with counts`);
    }
    
    // Get source counts
    let sourceQuery = supabase.from('NewsClipping').select('*');
    sourceQuery = applyFiltersExcept(sourceQuery, 'source');
    const { data: sourceData, error: sourceError } = await sourceQuery;
    
    if (sourceError) {
      console.error('[fetchFilterCounts] Error fetching source counts:', sourceError);
    } else if (sourceData) {
      // Count sources
      sourceData.forEach(item => {
        const source = item.Source;
        if (source) {
          counts.source[source] = (counts.source[source] || 0) + 1;
        }
      });
      console.log(`[fetchFilterCounts] Found ${Object.keys(counts.source).length} sources with counts`);
    }

    return counts;
  } catch (error) {
    console.error('[fetchFilterCounts] Error in fetchFilterCounts:', error);
    return {
      brand: {},
      category: {},
      sentiment: {},
      source: {}
    };
  }
}

// Function to create a new clip report
export async function createClipReport(reportData: {
  clip_id: number | string;
  user_id: string;
  message: string;
}) {
  const { clip_id, user_id, message } = reportData;
  const numericClipId = ensureNumericId(clip_id); // Ensure clip_id is a number

  const { data, error } = await supabase
    .from('clip_reports')
    .insert([
      {
        clip_id: numericClipId,
        user_id: user_id,
        message: message,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating clip report:', error);
    throw error;
  }

  return data; // Return the created report data
}

// Function to get a user's allowed brand names
export async function getUserAllowedBrandNames(userId: string): Promise<string[] | 'all' | 'none'> {
  if (!userId) {
    console.warn('[getUserAllowedBrandNames] No userId provided, returning "none".');
    return 'none';
  }

  try {
    // 1. Call the SQL function to get the user's brand access configuration
    const { data: brandAccessConfig, error: rpcError } = await supabase.rpc('get_user_brand_access', { p_user_id: userId });

    if (rpcError) {
      console.error(`[getUserAllowedBrandNames] RPC error fetching brand access for user ${userId}:`, rpcError);
      return 'none'; // Default to no access on error
    }

    if (!brandAccessConfig) {
      console.warn(`[getUserAllowedBrandNames] No brand access config returned for user ${userId}.`);
      return 'none';
    }

    const { type, ids: brandUuids } = brandAccessConfig as { type: 'all' | 'specific' | 'none', ids?: string[] };

    if (type === 'all') {
      console.log(`[getUserAllowedBrandNames] User ${userId} has access to ALL brands.`);
      return 'all';
    }

    if (type === 'none') {
      console.log(`[getUserAllowedBrandNames] User ${userId} has NO brand access.`);
      return 'none';
    }

    if (type === 'specific') {
      if (!brandUuids || brandUuids.length === 0) {
        console.warn(`[getUserAllowedBrandNames] User ${userId} has SPECIFIC access type but no brand UUIDs provided.`);
        return 'none'; // No specific brands means no access in this context
      }
      console.log(`[getUserAllowedBrandNames] User ${userId} has SPECIFIC access to brand UUIDs:`, brandUuids);
      // 2. Fetch brand names for these UUIDs
      const brandNames = await fetchBrandNamesByIds(brandUuids);
      console.log(`[getUserAllowedBrandNames] Fetched brand names for user ${userId}:`, brandNames);
      return brandNames;
    }

    console.warn(`[getUserAllowedBrandNames] Unknown brand access type "${type}" for user ${userId}.`);
    return 'none'; // Default to no access for unknown types

  } catch (error) {
    console.error(`[getUserAllowedBrandNames] Exception for user ${userId}:`, error);
    return 'none'; // Default to no access on exception
  }
}

// Helper function to map database fields to NewsClipping type
function mapDbToNewsClipping(data: any): NewsClipping {
  const rawSource = data.Source || data.source || 'Unknown Source';
  
  return {
    id: data.id,
    title: data.Title || data.title || 'Untitled Clip',
    source: rawSource,
    date: data.Date || data.date || new Date().toLocaleDateString(),
    content: data.Description || data.description,
    url: data.URL || data.url,
    category: data.Category || data.category,
    sentiment: data.Sentiment || data.sentiment,
    brandName: data['Brand name'] || data.brand_name || data.brandName,
    summary: data.Summary_ENG || data.summary_eng || data.summary,
    summary_original: data.Summary_Original || data.summary_original,
    user_id: data.user_id,
    image_url: data.image_url,
    quote_1: data.quote_1,
    quote_2: data.quote_2,
    quote_3: data.quote_3,
    local_brand_name: data.local_brand_name,
    visibility: data.Visibility || data.visibility,
    reach: data.Reach || data.reach,
    Language: data.Language || data.language,
    visitors: data.visitors,
    media_type: data.media_type,
  };
}
